'use client';

import { useCallback, useEffect, useState } from 'react';
import { subscriptionApi } from '@/lib/api';
import { useAuthStore } from '@/lib/auth-store';

export interface EffectiveEntitlements {
  plan_id: string | null;
  plan_key: string;
  plan_name: string;
  plan_rank: number;
  commission_rate: number;
  platform_fee_rate: number;
  platform_fee_percent: number;
  gst_rate: number;
  max_participants_per_activity: number | null;
  allowed_activity_types: string[];
  features: string[];
  paid_events_allowed: boolean;
  organization_id: string | null;
  organizer_id: string | null;
  overrides: Record<string, { is_allowed: boolean | null; limit_value: string | null; reason?: string }>;
}

export const PLAN_RANKS: Record<string, number> = {
  free: 0,
  bronze: 1,
  silver: 2,
  gold: 3,
  custom: 4,
  enterprise: 4,
};

const FEATURE_ALIASES: Record<string, string[]> = {
  attendance: ['attendance.management', 'attendance.check_in', 'attendance.qr'],
  'attendance.management': ['attendance'],
  communications: ['communication', 'communication.external.broadcast', 'email.campaigns'],
  communication: ['communications', 'communication.external.broadcast'],
  forms: ['forms.custom_builder', 'forms.registration'],
  'forms.custom_builder': ['forms'],
  team_registration: ['participant.team_registration', 'team-management'],
  'team-management': ['participant.team_registration', 'team_registration'],
  analytics: ['analytics.basic', 'analytics.advanced'],
  reports: ['analytics.export_reports', 'analytics.export'],
  certificates: ['certificates.bulk_generation', 'certificates.issue'],
  jury: ['jury.management', 'jury.panel_scoring', 'evaluation', 'evaluation-dashboard'],
  evaluation: ['jury', 'jury.management'],
  'evaluation-dashboard': ['jury', 'jury.management'],
  results: ['jury', 'jury.management', 'results'],
  paid_events: ['finance.paid_events', 'finance'],
  finance: ['finance.paid_events', 'paid_events'],
};

export function useEntitlements() {
  const [entitlements, setEntitlements] = useState<EffectiveEntitlements | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const authChecking = useAuthStore((state) => state.authChecking);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  const fetchEntitlements = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await subscriptionApi.current();
      const ent = res.data?.data?.entitlements;
      if (ent) {
        setEntitlements(ent);
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to fetch entitlements');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authChecking) return;
    if (!isAuthenticated) {
      setLoading(false);
      setEntitlements(null);
      return;
    }

    fetchEntitlements();

    const handlePlanUpdated = () => {
      fetchEntitlements();
    };

    window.addEventListener('plan:updated', handlePlanUpdated);
    return () => {
      window.removeEventListener('plan:updated', handlePlanUpdated);
    };
  }, [authChecking, isAuthenticated, fetchEntitlements]);

  const hasFeature = useCallback(
    (featureKey: string): boolean => {
      if (!entitlements) return false;
      const key = featureKey.toLowerCase().trim();

      // Direct check in effective features
      if (entitlements.features?.some((f) => f.toLowerCase() === key)) {
        return true;
      }

      // Check aliases
      const aliases = FEATURE_ALIASES[key] ?? [];
      for (const alias of aliases) {
        if (entitlements.features?.some((f) => f.toLowerCase() === alias)) {
          return true;
        }
      }

      return false;
    },
    [entitlements]
  );

  const canUseActivity = useCallback(
    (activityType: string): boolean => {
      if (!entitlements) return false;
      const act = activityType.toLowerCase().trim();
      if (act === 'ideathon' && entitlements.allowed_activity_types?.includes('idea_pitching')) {
        return true;
      }
      return (
        entitlements.allowed_activity_types?.map((a) => a.toLowerCase()).includes(act) ?? false
      );
    },
    [entitlements]
  );

  const isPlanAtLeast = useCallback(
    (requiredPlan: string): boolean => {
      if (!entitlements) return false;
      const currentRank =
        entitlements.plan_rank ??
        PLAN_RANKS[entitlements.plan_key?.toLowerCase() || 'silver'] ??
        2;
      const reqRank = PLAN_RANKS[requiredPlan.toLowerCase()] ?? 1;
      return currentRank >= reqRank;
    },
    [entitlements]
  );

  return {
    entitlements,
    loading,
    error,
    refetch: fetchEntitlements,
    hasFeature,
    canUseActivity,
    isPlanAtLeast,
    currentPlan: entitlements?.plan_key || 'silver',
    planName: entitlements?.plan_name || 'Silver',
    features: entitlements?.features ?? [],
  };
}
